// Shared scoring logic — extracted from src/App.js so the simulation script
// (scripts/simulate-season.mjs) can score exactly like the live app does.
// CommonJS so both webpack (browser) and Node can load it without a build step.
//
// Redesigned Sept 2026. The old formula blended "how much you beat the
// forecast" with "how big the opening was" as a continuous ratio+sqrt-scale
// calculation. That let a big film score well almost regardless of accuracy
// (a $165M film landing right on target scored more than most films ever
// could), and two rounds of patching it for small-estimate films kept
// over- or under-correcting in ways nobody caught until they showed up live.
// This version is a plain lookup: compare the real result to the estimate,
// find the matching band, that's the score. No hidden multipliers stacking
// on top of each other.
//
//   - Opening points are a flat bucket lookup based on actual vs estimate.
//     Meeting your number is worth 0. Beating it earns points. Missing it
//     costs points. Size of the film plays no part, so a $2M sleeper that
//     doubles its number scores the same as a $200M blockbuster that doubles
//     its number.
//   - A FLOP (opens below 60% of estimate, and misses by more than $2M in
//     real dollars) is a flat −40, no other modifier ever touches it.
//   - A film whose own estimate was under $5M can never score worse than a
//     Clear miss (−20), even on a total collapse. The real money at stake is
//     too small for the full flop penalty to be fair on a tiny film.
//   - Reviews are a flat, separate bonus: +10 for 70%+ on Rotten Tomatoes,
//     −10 for under 40%, nothing in between. Never applied to a real flop.
//   - LEGS points score everything a film makes after opening weekend, as a
//     multiple of its own opening. This replaced an earlier per-week
//     "held better than a typical drop" version that let two strong weeks
//     exhaust the whole bonus, so a film that kept genuinely overperforming
//     for the rest of its run earned nothing more. The multiple keeps
//     climbing for as long as the film keeps earning it, and it's sized to
//     matter: a true word-of-mouth phenomenon (3×+ its opening weekend) is
//     worth +70, in the same range as the opening score itself.

// A film's own estimate below this can never score worse than a Clear miss,
// and its price/ratio treatment gets the same floor — the real dollars at
// stake are too small for the harshest penalty to be fair. This replaced an
// earlier design that instead forgave any film whose real DOLLAR shortfall
// was small, which had a real exploit: it let a mid-size film ($8M+) dodge
// a flop whenever its shortfall happened to land under the dollar line,
// while an equally-tiny-stakes film just over the line got the full −40.
// Gating on the film's own estimate size instead closes that off — only
// genuinely small-stakes films get the floor, regardless of the shortfall.
const TINY_FLOOR_EST = 5 // $M
const FLOP_PENALTY = -40

const OPEN_BUCKETS = [
  { max: 0.70, label: 'Clear miss', pts: -20 },
  { max: 0.85, label: 'Soft miss', pts: -10 },
  { max: 1.05, label: 'On target', pts: 0 },
  { max: 1.20, label: 'Beat it a bit', pts: 15 },
  { max: 1.50, label: 'Solid beat', pts: 30 },
  { max: 2.50, label: 'Big beat', pts: 50 },
  { max: Infinity, label: 'Blew it away', pts: 80 },
]
function openBucketOf (ratio) {
  for (const b of OPEN_BUCKETS) if (ratio < b.max) return b
  return OPEN_BUCKETS[OPEN_BUCKETS.length - 1]
}
function reviewBonus (rt) {
  if (rt == null) return 0
  return rt >= 70 ? 10 : rt < 40 ? -10 : 0
}

// Full breakdown of a film's opening score — the UI reads this directly
// rather than recomputing pieces of the formula itself, so what's displayed
// can never drift from what actually got scored.
//   flop        — true only for a real, flat −40 flop
//   tinyFloor   — true if the under-$5M floor is what saved it from flopping
//   label       — the bucket name shown to players
//   ratio       — actual ÷ estimate
//   bucketPts   — points from the bucket alone, before reviews
//   reviewBonus — +10 / −10 / 0, always 0 on a real flop
//   total       — bucketPts + reviewBonus (== calcOpeningPts' return value)
function calcOpeningPtsBreakdown (film, actualM) {
  if (actualM == null || !film.estM) return null
  const ratio = actualM / film.estM
  let bucketPts, label, flop = false, tinyFloor = false
  if (ratio < 0.60) {
    if (film.estM < TINY_FLOOR_EST) { bucketPts = -20; label = 'Clear miss'; tinyFloor = true }
    else { flop = true; bucketPts = FLOP_PENALTY; label = 'Flop' }
  } else {
    const b = openBucketOf(ratio); bucketPts = b.pts; label = b.label
  }
  const rev = flop ? 0 : reviewBonus(film.rt)
  const total = bucketPts + rev
  return { flop, tinyFloor, label, ratio, bucketPts, reviewBonus: rev, total }
}

function calcOpeningPts (film, actualM) {
  const b = calcOpeningPtsBreakdown(film, actualM)
  return b ? b.total : 0
}

// True only for a real, flat −40 flop (not the softened tiny-film-floor
// case). Callers use this to skip legs/marquee treating a floored miss as
// if it were a full flop.
function isFlop (film, actualM) {
  const b = calcOpeningPtsBreakdown(film, actualM)
  return !!b && b.flop
}

// ── LEGS — everything made after opening, as a multiple of the opening
// weekend itself. weekMap: { 2: $M, 3: $M, ... 6: $M }
function postOpeningMultiple (openingM, weekMap) {
  if (!openingM) return null
  let post = 0, any = false
  for (let w = 2; w <= 6; w++) {
    const v = Number(weekMap && weekMap[w])
    if (v > 0) { post += v; any = true }
  }
  if (!any) return null
  return post / openingM
}
const LEGS_BANDS = [
  { max: 0.55, pts: 0, priceBump: 0 },
  { max: 0.85, pts: 10, priceBump: 0.05 },
  { max: 1.5, pts: 25, priceBump: 0.12 },
  { max: 3.0, pts: 45, priceBump: 0.20 },
  { max: Infinity, pts: 70, priceBump: 0.30 },
]
function legsBandOf (multiple) {
  if (multiple == null) return LEGS_BANDS[0]
  for (const b of LEGS_BANDS) if (multiple < b.max) return b
  return LEGS_BANDS[LEGS_BANDS.length - 1]
}
function calcWeeklyPts (weekMap, openingM) {
  return legsBandOf(postOpeningMultiple(openingM, weekMap)).pts
}

// ── legacy exports, kept only so scripts/mock-draft.mjs (which reimplements
// the pre-redesign formula itself for an old-vs-new comparison) still runs.
// Not used by calcOpeningPts/calcWeeklyPts above.
function rtMult (rt) {
  if (rt == null) return 1
  return rt >= 90 ? 1.15 : rt >= 80 ? 1.10 : rt >= 70 ? 1.05 : rt >= 60 ? 1.00 : rt >= 50 ? 0.95 : 0.90
}
function perfMult (r) {
  return r >= 2 ? 2 : r >= 1.5 ? 1.6 : r >= 1.3 ? 1.35 : r >= 1.1 ? 1.15 : r >= 0.95 ? 1 : r >= 0.8 ? 0.85 : r >= 0.6 ? 0.65 : 0.45
}
function calcWeeklyPtsCumulative (weekMap, openingM) {
  if (!openingM || openingM <= 0) return 0
  let post = 0
  for (let w = 2; w <= 6; w++) { const v = Number(weekMap && weekMap[w]); if (v > 0) post += v }
  if (post <= 0) return 0
  return Math.round(60 * Math.min(2.5, post / openingM))
}

module.exports = {
  calcOpeningPts, calcOpeningPtsBreakdown, calcWeeklyPts, isFlop,
  postOpeningMultiple, legsBandOf, FLOP_PENALTY, TINY_FLOOR_EST,
  rtMult, perfMult, calcWeeklyPtsCumulative,
}
